import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  inject,
  signal,
} from "@angular/core";
//import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { MatSort } from "@angular/material/sort";
import { MatPaginatorModule, MatPaginatorIntl } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { ActivatedRoute, Router, RouterLinkWithHref } from "@angular/router";
import {
  MatDialogModule,
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogConfig,
} from "@angular/material/dialog";
import { MsalService } from "@azure/msal-angular";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { ListMembersService } from "../../../data/member/list-members.service";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";

import dayjs from "dayjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

// For Dialog view
import {
  StaffDetailsComponent,
  StaffData,
} from "../staff-details/staff-details.component";
import { StaffRolesComponent } from "../staff-roles/staff-roles.component";
import { StaffActivationComponent } from "../staff-activation/staff-activation.component";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { TableSkeletonComponent } from "../../../shared/skeleton";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { STAFF_LIST_I18N } from "./staff-list.i18n";
import { COMMON_I18N } from '@app/i18n';

@Component({
  selector: "app-staff-list",
  templateUrl: "./staff-list.component.html",
  styleUrls: ["./staff-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS, TableSkeletonComponent, PageHeaderComponent],
})
export class StaffListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild("searchInput") searchInput: ElementRef<HTMLInputElement>;

  // MatSort setter pattern: automatically connect sort when table renders
  private _sort: MatSort;
  @ViewChild(MatSort)
  set sort(sort: MatSort) {
    this._sort = sort;
    if (this.dataSource && sort) {
      this.dataSource.sort = sort;
    }
  }
  get sort(): MatSort {
    return this._sort;
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly paginatorIntl = inject(MatPaginatorIntl);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = STAFF_LIST_I18N;

  loginUserId = "";
  filter = ""; //table filtering
  isLoading = signal(true);
  readonly searchExpanded = signal(false);

  selectedSection: any;
  currentUrl = "";
  userType = "";
  userTypeDisp = "";

  users: AccountUserData[] = [];
  displayedColumns = [
    "name",
    "userjob",
    "status",
    "createdDate",
    "actions",
  ];

  accountStatus: { [key: string]: string } = {
    OPEN: this.i18n.status.active,
    CLOSE: this.i18n.status.inactive,
  };

  //dialog with path param
  private dialogRef: MatDialogRef<any, any>;
  private routerUrl: string;

  dataSource: MatTableDataSource<AccountUserData>;

  constructor(
    public storeUser: StoreUserService,
    private route: ActivatedRoute,
    private listMembersService: ListMembersService,
    private router: Router,
    private msalService: MsalService,
    public dialog: MatDialog,
    private domSanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry,
    private common: DatabaseCacheService,
  ) {
    let user = this.msalService.instance.getActiveAccount();
    this.loginUserId = user.idTokenClaims["sub"];

    // カスタムSVG iconの呼び出し
    this.iconRegistry.addSvgIcon(
      "icon_staff", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_staff.svg",
      ), //必ずサニタイザーを利用して取得する。
    );

    this.iconRegistry.addSvgIcon(
      "icon_setting", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_setting.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-close", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-close.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-open", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-open.svg",
      ),
    );
  }

  ngOnInit() {
    this.configurePaginatorLabels();
    const userType = this.common.getUserType(this.router.url);

    this.userType = userType.userType;
    this.userTypeDisp = userType.userTypeDisp;

    //STAFF 及び Contributor などの一覧で表示を制御する
    // All user types use the same columns with actions menu
    this.displayedColumns = [
      "name",
      "userjob",
      "status",
      "createdDate",
      "actions",
    ];

    // section
    this.selectedSection = this.common.getSelectedSection();
    this.listMembers(this.userType);
    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((noData) => {
      // Sectionに関わるデータの読み込み
      this.selectedSection = this.common.getSelectedSection();
      this.listMembers(this.userType);
    });
  }

  ngAfterViewInit() {
    // Ensure sort and paginator are connected after view init
    if (this.dataSource) {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }
  }

  applyFilter(event?: Event) {
    if (event) {
      this.filter = (event.target as HTMLInputElement).value;
    }
    const filterValue = this.filter.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  // M3 Expandable Search methods
  expandSearch(): void {
    this.searchExpanded.set(true);
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  collapseSearch(): void {
    if (!this.filter.trim()) {
      this.searchExpanded.set(false);
    }
  }

  onSearchBlur(): void {
    this.collapseSearch();
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.filter = "";
    this.applyFilter();
  }

  refreshData(): void {
    this.listMembers(this.userType);
  }

  inviteStaff() {
    switch (this.userType) {
      case "STAFF":
        this.router.navigate([
          "/section-admin/",
          this.selectedSection.sectionId,
          "staff-invite",
        ]);
        break;
      case "CONTRIBUTOR":
        this.router.navigate([
          "/section-admin/",
          this.selectedSection.sectionId,
          "contributor-invite",
        ]);
        break;
      case "SUPERVISOR":
        this.router.navigate([
          "/section-admin/",
          this.selectedSection.sectionId,
          "supervisor-invite",
        ]);
        break;
    }
  }

  setStore(row) {
  }

  openStaffDetailsDialog(refid): void {
    const dialogRef = this.dialog.open(StaffDetailsComponent, {
      width: "740px",
      disableClose: false,
      data: {
        title: this.i18n.dialog.title,
        message: this.i18n.dialog.message,
        sectionId: this.selectedSection.sectionId,
        userId: refid,
        userTypeDisp: this.userTypeDisp,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (result) => {
      if (result?.needRefresh) {
        await this.common.refreshAllData(this.selectedSection?.sectionId);
        this.listMembers(this.userType);
      }
    });
  }

  openStaffRolesDialog(refid): void {
    const dialogRef = this.dialog.open(StaffRolesComponent, {
      width: "400px",
      disableClose: false,
      data: {
        title: this.i18n.dialog.title,
        message: this.i18n.dialog.message,
        id: refid,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {});
  }

  openStaffActivationDialog(refid): void {
    const dialogRef = this.dialog.open(StaffActivationComponent, {
      width: "400px",
      disableClose: false,
      data: {
        title: this.i18n.dialog.title,
        message: this.i18n.dialog.message,
        id: refid,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result?.needRefresh) {
        this.listMembers(this.userType);
      }
    });
  }

  getuserJobTitle(jobTitleCode) {
    return this.common.getJobTitle(jobTitleCode);
  }

  listMembers(userType) {
    this.isLoading.set(true);
    this.listMembersService
      .listMembers(this.selectedSection.sectionId, userType, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.users = [];
          for (let i = 0; i < (res as any).length; i++) {
            var date = dayjs((res as any)[i].contractInfo.contractStartAt);
            var dateStr = date.isValid() ? date.format("YYYY/MM/DD") : "ERROR";
            // var dateStr = (date.isValid()) ? date.format('YYYY/MM/DD [at] HH[h]') : 'ERROR';
            var userjobCode = "";
            if (
              (res as any)[i].attributes == null ||
              (res as any)[i].attributes == ""
            ) {
              userjobCode = "OTHER";
            } else {
              userjobCode = (res as any)[i].attributes.jobTitleCode;
            }

            let userId = (res as any)[i].user.userId;

            if (userId === this.loginUserId) {
              let userName =
                (res as any)[i].user.lastName +
                " " +
                (res as any)[i].user.firstName;
              this.users.unshift({
                id: userId,
                name: userName,
                createdDate: dateStr,
                status: (res as any)[
                  i
                ].contractInfo.contractStatus.toUpperCase(),
                userjob: userjobCode,
              });
            } else {
              this.users.push({
                id: userId,
                name:
                  (res as any)[i].user.lastName +
                  " " +
                  (res as any)[i].user.firstName,
                createdDate: dateStr,
                status: (res as any)[
                  i
                ].contractInfo.contractStatus.toUpperCase(),
                userjob: userjobCode,
              });
            }
          }
          this.dataSource = new MatTableDataSource(this.users);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;

          // Ensure sort works correctly for all columns
          this.dataSource.sortingDataAccessor = (item: AccountUserData, property: string) => {
            switch (property) {
              case 'status':
                // OPEN = 1, CLOSE = 0 for proper ordering (OPEN first in desc)
                return item.status === 'OPEN' ? 1 : 0;
              case 'name':
                return item.name.toLowerCase();
              case 'userjob':
                return this.getuserJobTitle(item.userjob)?.toLowerCase() || '';
              case 'createdDate':
                return item.createdDate;
              default:
                return (item as any)[property];
            }
          };

          this.isLoading.set(false);
        },
        (error) => {
          this.isLoading.set(false);
        },
      );
  }

  private configurePaginatorLabels(): void {
    this.paginatorIntl.itemsPerPageLabel = COMMON_I18N.paginator.itemsPerPage;
    this.paginatorIntl.nextPageLabel = COMMON_I18N.paginator.nextPage;
    this.paginatorIntl.previousPageLabel = COMMON_I18N.paginator.previousPage;
    this.paginatorIntl.firstPageLabel = COMMON_I18N.paginator.firstPage;
    this.paginatorIntl.lastPageLabel = COMMON_I18N.paginator.lastPage;
    this.paginatorIntl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ) => {
      if (length === 0 || pageSize === 0) {
        return `0 / ${length}`;
      }
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;
      return `${startIndex + 1} – ${endIndex} / ${length}`;
    };
  }
}

export interface AccountUserData {
  id: string;
  createdDate: string;
  name: string;
  status: string;
  userjob: string;
}
